// Gradient profile hero used at the top of Customer 360.
//
// Matches the report-page hero language (Glass Depth: gradient + blurred
// orbs + backdrop-blur chips) so the admin app feels consistent across
// domains. CRM domain palette per CLAUDE.md: violet → fuchsia → pink.

import type { ReactNode } from 'react';
import { Avatar } from '@bbpos/ui';
import type { Customer } from './types';
import { segmentMeta } from './segments';
import { customerInitials, formatPhone } from './format';

export interface CustomerHeroProfileProps {
  customer: Customer;
  actions?: ReactNode;
}

export function CustomerHeroProfile({ customer, actions }: CustomerHeroProfileProps) {
  const meta = segmentMeta(customer.rfm_segment);

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-fuchsia-500 to-pink-500 p-6 sm:p-8 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(168,85,247,0.6)] animate-fade-in">
      {/* Glass-depth decorative orbs */}
      <div className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-white/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-black/15 blur-3xl" />

      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4 sm:gap-5 min-w-0">
          {/* Large avatar in a white ring */}
          <div className="flex-shrink-0 rounded-2xl bg-white/15 p-1 ring-4 ring-white/20 backdrop-blur-sm">
            <Avatar name={customer.name || customerInitials(customer.name)} size="lg" shape="rounded" />
          </div>

          <div className="min-w-0">
            {meta ? (
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider backdrop-blur-sm ${meta.chip}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                {meta.label}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white/90 ring-1 ring-white/25 backdrop-blur-sm">
                Unsegmented
              </span>
            )}
            {customer.is_active === false ? (
              <span className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-neutral-900/40 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white ring-1 ring-white/30 backdrop-blur-sm">
                Inactive
              </span>
            ) : null}
            <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight truncate">{customer.name}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/85">
              <span className="tabular-nums">{formatPhone(customer.phone)}</span>
              {customer.email ? <span className="truncate">{customer.email}</span> : null}
            </div>
            {customer.city || customer.state ? (
              <p className="mt-1 text-xs text-white/70">
                {[customer.city, customer.state].filter(Boolean).join(', ')}
              </p>
            ) : null}
          </div>
        </div>

        {actions ? (
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">{actions}</div>
        ) : null}
      </div>
    </div>
  );
}
